import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  FileText,
  Trash2,
  Loader2,
  Edit2,
  Check,
  Eye,
  Palette,
  Copy,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  getCertificateTemplates,
  createCertificateTemplate,
  updateCertificateTemplate,
  deleteCertificateTemplate,
  copyCertificateTemplate,
} from "../../../../api/elearning/certificates/certificates";
import { toast } from "react-toastify";
import TemplateDesignerModal from "./TemplateDesignerModal";
import { confirmDelete, DELETE_ENTITY } from "../../../../utils/confirmDelete";

const TemplateManageModal = ({ isOpen, onClose, onSuccess }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copyingId, setCopyingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [designerTemplate, setDesignerTemplate] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    recipient_type: "both",
  });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const d = await getCertificateTemplates("all");
      setTemplates(d.templates || []);
    } catch {
      toast.error("Không thể tải danh sách biểu mẫu.");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchTemplates();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Nhập tên biểu mẫu.");
      return;
    }
    try {
      if (editing) {
        await updateCertificateTemplate(editing.id, form);
        toast.success("Cập nhật biểu mẫu thành công.");
      } else {
        await createCertificateTemplate(form);
        toast.success("Tạo biểu mẫu thành công.");
      }
      setEditing(null);
      setForm({ name: "", description: "", recipient_type: "both" });
      fetchTemplates();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Lỗi xử lý.");
    }
  };

  const handleDelete = async (template) => {
    const id = typeof template === "object" ? template?.id : template;
    const name = typeof template === "object" ? template?.name : undefined;
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.certificate_template,
      name,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteCertificateTemplate(id);
      if (editing?.id === id) {
        cancelEdit();
      }
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Đã vô hiệu hóa biểu mẫu.");
      await fetchTemplates();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể xóa.");
    }
  };

  const startEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      description: t.description || "",
      recipient_type: t.recipient_type || "both",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ name: "", description: "", recipient_type: "both" });
  };

  const handleCopy = async (template) => {
    if (copyingId) return;
    setCopyingId(template.id);
    try {
      const created = await copyCertificateTemplate(template.id);
      toast.success(`Đã tạo bản sao: ${created.name}`);
      await fetchTemplates();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể sao chép biểu mẫu.");
    } finally {
      setCopyingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <TemplateDesignerModal
      isOpen={!!designerTemplate}
      template={designerTemplate}
      onClose={() => setDesignerTemplate(null)}
      onSaved={() => {
        fetchTemplates();
        onSuccess?.();
      }}
    />
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-xl overflow-hidden flex flex-col max-h-[85vh] z-[110]"
        >
          <div className="p-8 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-black text-gray-800 uppercase">
                Quản lý <span className="text-red-700">Biểu mẫu</span>
              </h2>
              <p className="text-gray-400 text-xs mt-1">
                Tạo biểu mẫu, sau đó mở Template Designer để gắn ảnh nền và vị trí trường dữ liệu
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-400"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 border-b border-gray-50 space-y-4">
            <div>
              <label className="text-[11px] font-bold text-gray-400 uppercase">Tên biểu mẫu</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="VD: Chứng nhận hoàn thành khóa học"
                className="w-full mt-1 bg-gray-50 border border-gray-200 py-2.5 px-4 rounded-xl text-sm font-bold outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold text-gray-400 uppercase">Mô tả</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Mô tả ngắn (tùy chọn)"
                className="w-full mt-1 bg-gray-50 border border-gray-200 py-2.5 px-4 rounded-xl text-sm outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold text-gray-400 uppercase">Đối tượng áp dụng</label>
              <select
                value={form.recipient_type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, recipient_type: e.target.value }))
                }
                className="w-full mt-1 bg-gray-50 border border-gray-200 py-2.5 px-4 rounded-xl text-sm font-bold outline-none focus:border-red-500"
              >
                <option value="both">Cả học viên và giáo viên</option>
                <option value="student">Chỉ học viên</option>
                <option value="teacher">Chỉ giáo viên</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-6 py-2.5 bg-red-700 text-white rounded-xl font-bold text-sm hover:bg-red-800 flex items-center gap-2"
              >
                {editing ? (
                  <>
                    <Check size={16} /> Lưu thay đổi
                  </>
                ) : (
                  <>
                    <Plus size={16} /> Thêm biểu mẫu
                  </>
                )}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-300"
                >
                  Hủy
                </button>
              )}
            </div>
          </form>

          <div className="flex-1 overflow-y-auto p-8">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 size={32} className="animate-spin text-red-600" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText size={48} className="mx-auto mb-4 opacity-50" />
                <p className="font-bold">Chưa có biểu mẫu</p>
                <p className="text-sm mt-1">Tạo biểu mẫu đầu tiên ở form trên</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {templates.map((t) => (
                  <li
                    key={t.id}
                    className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                      editing?.id === t.id
                        ? "bg-red-50 border-red-200"
                        : "bg-gray-50 border-gray-100 hover:bg-gray-100/70"
                    }`}
                  >
                    <div>
                      <p className="font-bold text-gray-800">{t.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {t.recipient_type === "both"
                          ? "Học viên & Giáo viên"
                          : t.recipient_type === "student"
                          ? "Học viên"
                          : "Giáo viên"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleCopy(t)}
                        disabled={copyingId === t.id}
                        className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Sao chép biểu mẫu"
                      >
                        {copyingId === t.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDesignerTemplate(t)}
                        className="p-2 text-violet-700 hover:bg-violet-50 rounded-lg transition-colors"
                        title="Template Designer — ảnh nền & vị trí trường"
                      >
                        <Palette size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(t)}
                        className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Xem lại / Chỉnh sửa"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(t)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Sửa thông tin"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(t)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Vô hiệu hóa"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
    </>
  );
};

export default TemplateManageModal;
